import {
  CIRCUIT_BREAKER_FAILURE_THRESHOLD,
  CIRCUIT_BREAKER_HTTP_CODES,
  CIRCUIT_BREAKER_PAUSE_MS,
  POLLING_IDLE_TIMEOUT_MS,
} from './pollingConstants';

export type PollingEndpointKey =
  | 'conversations-unread'
  | 'conversations-list'
  | 'conversation-detail'
  | 'found-object-stats';

type CircuitState = {
  consecutiveFailures: number;
  pausedUntil: number;
};

const circuitByEndpoint = new Map<PollingEndpointKey, CircuitState>();

function getCircuit(key: PollingEndpointKey): CircuitState {
  const existing = circuitByEndpoint.get(key);
  if (existing) return existing;
  const created = { consecutiveFailures: 0, pausedUntil: 0 };
  circuitByEndpoint.set(key, created);
  return created;
}

export function extractHttpStatus(error: unknown): number | undefined {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return typeof status === 'number' ? status : undefined;
}

export function recordPollingSuccess(key: PollingEndpointKey): void {
  const circuit = getCircuit(key);
  circuit.consecutiveFailures = 0;
  circuit.pausedUntil = 0;
}

export function recordPollingFailure(key: PollingEndpointKey, error: unknown): void {
  const status = extractHttpStatus(error);
  if (status !== undefined && !CIRCUIT_BREAKER_HTTP_CODES.has(status)) {
    return;
  }

  const circuit = getCircuit(key);
  circuit.consecutiveFailures += 1;
  if (circuit.consecutiveFailures >= CIRCUIT_BREAKER_FAILURE_THRESHOLD) {
    circuit.pausedUntil = Date.now() + CIRCUIT_BREAKER_PAUSE_MS;
    circuit.consecutiveFailures = 0;
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[PollingManager] Pause ${key} pendant ${CIRCUIT_BREAKER_PAUSE_MS / 1000}s`);
    }
  }
}

export function isEndpointCircuitOpen(key: PollingEndpointKey): boolean {
  const circuit = getCircuit(key);
  if (circuit.pausedUntil <= Date.now()) {
    return false;
  }
  return true;
}

export function resolvePollIntervalMs(
  baseIntervalMs: number | false,
  endpointKey: PollingEndpointKey,
  allowPolling: boolean,
): number | false {
  if (!baseIntervalMs || !allowPolling || isEndpointCircuitOpen(endpointKey)) {
    return false;
  }
  return baseIntervalMs;
}

/** Écouteurs globaux visibilité + inactivité (une seule instance). */
type PollingGateListener = (gate: { documentVisible: boolean; userActive: boolean }) => void;

let documentVisible = typeof document !== 'undefined' ? !document.hidden : true;
let userActive = true;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let listenersBound = false;
const gateListeners = new Set<PollingGateListener>();

function emitGate(): void {
  const snapshot = { documentVisible, userActive };
  gateListeners.forEach((listener) => listener(snapshot));
}

function resetIdleTimer(): void {
  userActive = true;
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    userActive = false;
    emitGate();
  }, POLLING_IDLE_TIMEOUT_MS);
}

function bindGlobalListeners(): void {
  if (listenersBound || typeof document === 'undefined') return;
  listenersBound = true;

  document.addEventListener('visibilitychange', () => {
    documentVisible = !document.hidden;
    emitGate();
  });

  const onActivity = () => {
    if (!userActive) {
      userActive = true;
      emitGate();
    }
    resetIdleTimer();
  };

  const opts: AddEventListenerOptions = { capture: true, passive: true };
  ['pointerdown', 'keydown', 'mousemove', 'scroll', 'touchstart'].forEach((eventName) => {
    document.addEventListener(eventName, onActivity, opts);
  });

  resetIdleTimer();
}

export function subscribePollingGate(listener: PollingGateListener): () => void {
  bindGlobalListeners();
  gateListeners.add(listener);
  listener({ documentVisible, userActive });
  return () => gateListeners.delete(listener);
}

export function isPollingAllowed(): boolean {
  return documentVisible && userActive;
}
